import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2, Lightbulb, TrendingUp, TrendingDown, Users, ShieldCheck,
  BarChart2, ArrowDownRight, BadgeDollarSign, ChevronRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import SectionNav from '../../../components/layout/SectionNav';
import { discoverTabs } from '../../../nav/publicNav';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/india`;

const STREAM_CONFIG = {
  promoter_buying: {
    title: 'Promoter Buying',
    icon: ShieldCheck,
    color: 'text-emerald-600',
    bg: 'bg-emerald-500/10',
    description: 'Promoters increasing stake QoQ',
  },
  fii_buying: {
    title: 'FII Buying',
    icon: Users,
    color: 'text-blue-600',
    bg: 'bg-blue-500/10',
    description: 'FII holdings increasing QoQ',
  },
  insider_buying: {
    title: 'Insider Buying',
    icon: BadgeDollarSign,
    color: 'text-amber-600',
    bg: 'bg-amber-500/10',
    description: 'Recent insider acquisitions',
  },
  margin_expansion: {
    title: 'Margin Expansion',
    icon: TrendingUp,
    color: 'text-purple-600',
    bg: 'bg-purple-500/10',
    description: 'High OPM with strong ROE',
  },
  deleveraging: {
    title: 'Deleveraging',
    icon: ArrowDownRight,
    color: 'text-cyan-600',
    bg: 'bg-cyan-500/10',
    description: 'High ROCE, strong return metrics',
  },
  price_pessimism: {
    title: 'Price Pessimism',
    icon: BarChart2,
    color: 'text-orange-600',
    bg: 'bg-orange-500/10',
    description: 'Strong fundamentals at low PE',
  },
};

function formatMcap(val) {
  if (!val) return null;
  const cr = val / 1e7;
  if (cr >= 100000) return `₹${(cr / 100000).toFixed(1)}L Cr`;
  if (cr >= 1000) return `₹${(cr / 1000).toFixed(1)}K Cr`;
  return `₹${cr.toFixed(0)} Cr`;
}

function McapBadge({ label }) {
  if (!label) return null;
  const colors = {
    'Large cap': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    'Mid cap': 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    'Small cap': 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    'Micro cap': 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    'Nano cap': 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400',
  };
  return (
    <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${colors[label] || 'bg-muted text-muted-foreground'}`}>
      {label}
    </span>
  );
}

function StreamMetric({ label, value, suffix = '', positive }) {
  if (value == null) return null;
  return (
    <div className="text-center">
      <div className={`text-xs font-semibold ${positive === true ? 'text-emerald-600' : positive === false ? 'text-red-500' : 'text-foreground'}`}>
        {positive === true && '+'}{typeof value === 'number' ? value.toFixed(1) : value}{suffix}
      </div>
      <div className="text-[9px] text-muted-foreground">{label}</div>
    </div>
  );
}

function IdeaCard({ item, streamKey, onClick }) {
  const renderMetrics = () => {
    switch (streamKey) {
      case 'promoter_buying':
        return (
          <>
            <StreamMetric label="Change" value={item.change_pct} suffix="%" positive={item.change_pct > 0} />
            <StreamMetric label="Latest" value={item.pct_latest} suffix="%" />
          </>
        );
      case 'fii_buying':
        return (
          <>
            <StreamMetric label="FII Chg" value={item.fii_change} suffix="%" positive={item.fii_change > 0} />
            <StreamMetric label="FII" value={item.fii_latest} suffix="%" />
          </>
        );
      case 'insider_buying':
        return <div className="text-[10px] text-muted-foreground truncate max-w-[120px]">{item.date}</div>;
      case 'margin_expansion':
        return (
          <>
            <StreamMetric label="OPM" value={item.opm} suffix="%" />
            <StreamMetric label="ROE" value={item.roe} suffix="%" />
          </>
        );
      case 'deleveraging':
        return (
          <>
            <StreamMetric label="ROCE" value={item.roce} suffix="%" />
            <StreamMetric label="ROE" value={item.roe} suffix="%" />
          </>
        );
      case 'price_pessimism':
        return (
          <>
            <StreamMetric label="PE" value={item.pe} />
            <StreamMetric label="ROE" value={item.roe} suffix="%" />
          </>
        );
      default:
        return null;
    }
  };

  return (
    <button onClick={onClick}
      className="flex items-center justify-between p-2.5 rounded-lg hover:bg-muted/30 transition-colors text-left w-full group">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold truncate">{item.company_name}</span>
          <span className="text-[10px] text-muted-foreground">{item.symbol}</span>
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <McapBadge label={item.mcap_label} />
          {item.sector && <span className="text-[10px] text-muted-foreground">{item.sector}</span>}
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        {renderMetrics()}
        <ChevronRight className="w-3 h-3 text-muted-foreground/30 group-hover:text-emerald-500 transition-colors" />
      </div>
    </button>
  );
}

function StreamSection({ streamKey, data, navigate, expanded, onToggle }) {
  const config = STREAM_CONFIG[streamKey];
  if (!config || !data) return null;
  const Icon = config.icon;
  const items = data.items || [];
  const displayItems = expanded ? items : items.slice(0, 5);

  return (
    <Card className="border-border/50">
      <CardHeader className="pb-2 pt-4 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-lg ${config.bg} flex items-center justify-center`}>
              <Icon className={`w-3.5 h-3.5 ${config.color}`} />
            </div>
            <div>
              <CardTitle className="text-sm">{data.title || config.title}</CardTitle>
              <p className="text-[10px] text-muted-foreground">{data.description || config.description}</p>
            </div>
          </div>
          <Badge variant="secondary" className="text-[10px]">{items.length}</Badge>
        </div>
      </CardHeader>
      <CardContent className="px-2 pb-3">
        {items.length === 0 ? (
          <div className="text-center py-6 text-xs text-muted-foreground">No data available</div>
        ) : (
          <>
            <div className="divide-y divide-border/30">
              {displayItems.map((item, i) => (
                <IdeaCard
                  key={item.isin + i}
                  item={item}
                  streamKey={streamKey}
                  onClick={() => item.symbol && navigate(`/public/india-company/${item.symbol}`)}
                />
              ))}
            </div>
            {items.length > 5 && (
              <button
                onClick={onToggle}
                className="w-full text-center text-xs text-emerald-600 hover:text-emerald-500 font-medium mt-2 py-1.5"
              >
                {expanded ? 'Show less' : `View all ${items.length}`}
              </button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function IdeasDashboardPage() {
  const navigate = useNavigate();
  const [streams, setStreams] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [activeFilter, setActiveFilter] = useState('all');

  const fetchIdeas = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/ideas?stream=all&limit=15`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setStreams(data.streams || {});
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchIdeas(); }, [fetchIdeas]);

  const toggleExpand = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  const streamKeys = Object.keys(STREAM_CONFIG);
  const filteredKeys = activeFilter === 'all' ? streamKeys : [activeFilter];

  const totalIdeas = Object.values(streams).reduce((sum, s) => sum + (s?.items?.length || 0), 0);

  return (
    <div className="space-y-4">
      <SectionNav tabs={discoverTabs} />
      <div>
        <h1 className="text-xl font-bold flex items-center gap-2">
          <Lightbulb className="w-5 h-5 text-emerald-500" /> Ideas Dashboard
        </h1>
        <p className="text-xs text-muted-foreground">
          Curated investment ideas from promoter moves, institutional flows, and fundamentals
          {totalIdeas > 0 && <span className="ml-1">· {totalIdeas} ideas</span>}
        </p>
      </div>

      {/* Stream filter pills */}
      <div className="flex flex-wrap gap-1.5">
        <Button
          size="sm" variant={activeFilter === 'all' ? 'default' : 'outline'}
          className={`text-xs h-7 ${activeFilter === 'all' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}`}
          onClick={() => setActiveFilter('all')}
        >
          All Streams
        </Button>
        {streamKeys.map(key => {
          const config = STREAM_CONFIG[key];
          const Icon = config.icon;
          return (
            <Button
              key={key}
              size="sm"
              variant={activeFilter === key ? 'default' : 'outline'}
              className={`text-xs h-7 ${activeFilter === key ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}`}
              onClick={() => setActiveFilter(key)}
            >
              <Icon className="w-3 h-3 mr-1" />
              {config.title}
            </Button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      ) : error ? (
        <div className="text-red-500 text-sm p-4 bg-red-50 dark:bg-red-950/20 rounded-lg">{error}</div>
      ) : (
        <div className={`grid gap-4 ${activeFilter === 'all' ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 max-w-2xl'}`}>
          {filteredKeys.map(key => (
            <StreamSection
              key={key}
              streamKey={key}
              data={streams[key]}
              navigate={navigate}
              expanded={!!expanded[key]}
              onToggle={() => toggleExpand(key)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
